/**
 * The `/link` contract — the web tier's SECOND sanctioned write, and the only one a
 * non-staff person will ever cause.
 *
 * WHAT A LINK IS. A player runs `/link` in game. The mod mints a code, remembers it IN ITS OWN
 * MEMORY, and pushes `(code, playerUuid, ttl)` to the sidecar. The player then signs in with
 * Discord and types the code; the sidecar files one PENDING `web_outbox` row, action
 * `link_discord`, carrying the code and the snowflake it AUTHENTICATED. The mod's drain claims
 * that row, checks the code against its own book, writes `player_profile.discord_id` and audits
 * it. 202 from the claim endpoint means "filed", never "linked".
 *
 * WHY THE SIDECAR CANNOT BIND, STATED AS A PRIVILEGE AND NOT AS A PROMISE. `web-roles.sql`
 * grants `web_write` INSERT on `player_profile(player_uuid, created_at, last_seen)` and
 * explicitly `revoke update, delete, truncate on table public.player_profile`. There is no
 * column-level UPDATE grant and there must never be one: the whole point of routing the binding
 * through the outbox is that the identity column has exactly one writer, the mod, and that a
 * stolen `DATABASE_URL` buys the ability to ASK for a link rather than to make one.
 * `deploy/dev-stack.sh` proves that revoke with psql on every run.
 *
 * WHY THE MOD, NOT THE SIDECAR, IS THE AUTHORITY ON CODES. The sidecar keeps a copy of each
 * pending code so it can resolve `web_outbox.target_id` (the column is NOT NULL and its RLS
 * policy requires it to name a real profile). That copy is a ROUTING TABLE, not a gate: the mod
 * re-checks the code against its own book AND checks that the row's target is the player the
 * code was minted for. So a compromised sidecar filing a `link_discord` intent for an arbitrary
 * player and an arbitrary snowflake is refused by the game server, with a reason, and nothing is
 * written. That is the property the whole design exists for.
 *
 * WHAT IS DELIBERATELY NOT HERE. No unlink. No list-my-links. No "which player is this
 * snowflake" lookup. The first would need an UPDATE the write role does not hold; the second and
 * third are reads, and reads go through the read plane (`web.player_directory.discord_id`) under
 * a role whose grants are declared in one place. An endpoint per question would be a second
 * privilege surface for something the existing one already answers.
 */
import type { UuidText } from './views';

/**
 * The `web_outbox.action` a link is written as.
 *
 * `WebIntents.ACTION_LINK_DISCORD` is the other end of this string, and the mod refuses any
 * action it does not recognise BY NAME — so a sidecar deployed ahead of the mod fails visibly
 * instead of filing intents nobody applies.
 *
 * Underscored rather than dotted like `reward.grant`. That is the name the contract was written
 * against, and the string is pinned at four independent ends (here, the mod's constant, the
 * sidecar's INSERT and every audit row already written), so a cosmetic rename would be a
 * migration of stored rows for no behavioural gain.
 */
export const LINK_ACTION = 'link_discord';

/**
 * The alphabet a link code is drawn from: Crockford base32 — digits and upper-case letters with
 * `I`, `L`, `O` and `U` removed.
 *
 * Exactly 32 symbols, which is what lets the mod index it with 5 raw bits per character and no
 * modulo bias. The omissions are the ones a person mistypes (`I`/`1`, `O`/`0`, `L`/`1`) plus `U`,
 * which Crockford drops so the alphabet cannot spell an obscenity. Unlike a session code — which
 * an admin copies out of a URL — this one is TYPED BY A PLAYER, so readability is a requirement.
 *
 * Mirrors `LinkCodeService.CODE_ALPHABET`. The mod is the only minter; this copy exists so the
 * sidecar can refuse a malformed code without a round trip, and so the SPA can validate as the
 * player types.
 */
export const LINK_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Length of a link code, and therefore its entropy: 10 × log2(32) = **50 bits**.
 *
 * Mirrors `LinkCodeService.CODE_LENGTH`, where the reasoning for that number lives. In short: a
 * guess must hit a code that is live right now, inside a ten-minute window, through an
 * authenticated endpoint, for a reward of binding your own Discord account to a stranger's game
 * account — which the audit row records.
 */
export const LINK_CODE_LENGTH = 10;

/** Anchored: a code is exactly {@link LINK_CODE_LENGTH} characters of the alphabet, nothing else. */
const LINK_CODE_PATTERN = new RegExp(`^[${LINK_CODE_ALPHABET}]{${LINK_CODE_LENGTH}}$`);

/**
 * True for a string that could be a link code.
 *
 * CASE-SENSITIVE, and callers normalise BEFORE testing rather than after. The mod's book is keyed
 * by the exact upper-case string it minted, so a lower-case spelling that reached the outbox
 * would come back as "well-formed but unknown" — which reads to a player as their correct code
 * being rejected. {@link normaliseLinkCode} is where the one normalisation happens.
 */
export function isLinkCode(value: unknown): value is string {
  return typeof value === 'string' && LINK_CODE_PATTERN.test(value);
}

/**
 * Turns what a person typed into what the mod minted, or `null` if it cannot be one.
 *
 * Upper-cases and strips the separators a human adds unprompted — spaces, hyphens and the
 * zero-width characters a copy-paste from Discord can carry. It deliberately does NOT map `O` to
 * `0` or `I` to `1`: those characters are absent from the alphabet precisely so that a mistyped
 * code fails loudly here instead of silently becoming a DIFFERENT valid code, which on a busy
 * server is somebody else's.
 */
export function normaliseLinkCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  // \u200B-\u200D and \uFEFF are the zero-width family: spelled as escapes rather than pasted,
  // because a literal invisible character in a regex is a character class nobody can review.
  const cleaned = value.replace(/[\s\-\u200B-\u200D\uFEFF]+/g, '').toUpperCase();
  return isLinkCode(cleaned) ? cleaned : null;
}

/**
 * How long the sidecar will hold a pending code, at most, whatever TTL the mod asks for.
 *
 * The MOD's book is the authority — the sidecar's copy lapsing early only means a claim gets
 * "unknown code" a little sooner than it would have got "expired". This cap exists so a
 * misconfigured or hostile push cannot pin memory in the sidecar for a week.
 */
export const LINK_MAX_TTL_SECONDS = 30 * 60;

/** Floor, for the same reason: a zero or negative TTL is a code nobody could ever type in time. */
export const LINK_MIN_TTL_SECONDS = 30;

/**
 * Cap on how many pending codes the sidecar will hold at once.
 *
 * A full store refuses a push with 429 rather than evicting somebody else's pending link — the
 * same stance `SESSION_MAX_CONCURRENT` takes, for the same reason: silently dropping a code the
 * player is about to type is indistinguishable, from their side, from the code never working.
 */
export const LINK_MAX_PENDING = 512;

/** Longest snowflake accepted anywhere in the web tier. 20 digits is the real maximum. */
export const DISCORD_ID_MAX_LENGTH = 32;

/** True for a plausible Discord snowflake: 1..32 decimal digits and nothing else. */
export function isDiscordId(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9]{1,32}$/.test(value) && value.length <= DISCORD_ID_MAX_LENGTH;
}

/** Version stamped on both link request bodies; shares the session protocol's generation. */
export const LINK_VERSION = 1;

/** Cap on either link request body. Both are a handful of short fields. */
export const LINK_MAX_BYTES = 4 * 1024;

/** SPA path the player is sent to. The sidecar builds the URL the mod prints; the SPA routes on it. */
export const LINK_PATH = '/link';

/** Builds the page URL a player is handed. `origin` may carry a trailing slash; it is dropped. */
export function linkPageUrl(origin: string): string {
  return `${origin.replace(/\/+$/, '')}${LINK_PATH}`;
}

/**
 * `POST /v1/link` — what the GAME SERVER pushes (Bearer `serverToken`).
 *
 * `ttlSeconds` rather than an absolute instant because the two ends do not share a clock: a
 * pushed `expiresAt` would silently mean something different on a sidecar whose time had drifted,
 * while a duration is interpreted by each side against its own.
 */
export interface LinkCodePush {
  readonly version: typeof LINK_VERSION;
  readonly code: string;
  readonly playerUuid: UuidText;
  readonly ttlSeconds: number;
}

/** The sidecar's answer to a push: the code it stored, the page to open, and its own expiry. */
export interface LinkCodeStored {
  readonly code: string;
  readonly url: string;
  /** Epoch MILLISECONDS on the sidecar's clock. */
  readonly expiresAt: number;
}

/**
 * `POST /v1/link/claim` — what the BROWSER sends (Bearer: the Worker-signed session token).
 *
 * There is no `discordId` field, and its absence is the security property: the snowflake comes
 * off the token the sidecar VERIFIED, never off this body. A caller who could name their own
 * snowflake could link any Discord account to any player whose code they held.
 *
 * There is no `playerUuid` field either: the target is resolved from the sidecar's own copy of the
 * mod's push, so a claim cannot aim a code at a player it was not minted for. The mod re-checks
 * that anyway.
 */
export interface LinkClaimRequest {
  readonly version: typeof LINK_VERSION;
  readonly code: string;
}

/**
 * 202 from the claim: the intent is FILED, not applied.
 *
 * `playerUuid` is echoed so the page can say which account is being linked — the claimer holds a
 * code that player minted in game, so this reveals nothing they did not already prove. Following
 * the intent from here is `web.outbox_status`'s job, exactly as it is for a grant.
 */
export interface LinkClaimAccepted {
  readonly ok: true;
  readonly playerUuid: UuidText;
}

/** Stable machine-readable refusals. The SPA maps these to wording; nothing branches on prose. */
export type LinkErrorCode =
  | 'unauthorised'
  | 'forbidden'
  | 'invalid_request'
  | 'invalid_code'
  | 'unknown_code'
  | 'payload_too_large'
  | 'too_many_pending'
  | 'link_not_configured'
  | 'database_error'
  | 'method_not_allowed';

export interface LinkRejected {
  readonly error: LinkErrorCode;
  readonly detail?: string;
}
